import { useContext, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import {
  arePricesLoading,
  isBNBLAvailable,
  getCheapestAvailablePackageOption,
  getFromDurationLabel,
  getEffectiveCharterBundleRoute,
  getEffectiveBundledWithFlightsOnly,
} from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'LuxKit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import Group from 'components/utils/Group'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { sortBy } from 'lib/array/arrayUtils'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import type { MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getActivePackagesByRoom, getMultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import FlashHotelWePriceMatch from '../FlashHotelWePriceMatch'
import GeoContext from 'contexts/geoContext'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { CHARTER_FLIGHT_DETAILS, isMaldivesCharterBundleOffer } from 'constants/charterFlight'
import { addFlightPrice } from 'lib/offer/pricing'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { isRewardFareClass, type FlightsClassTypes } from 'constants/flight'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import PriceRowValueDiscountWithCaptionLoadingSkeleton from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaptionLoadingSkeleton'
import { useIntl } from 'react-intl'
import useTravelSynonymLabels from 'hooks/useTravelSynonymLabels'
import productLabels from 'i18n/productLabels'
import FlashHotelFlightDealMerchandisingSlot from '../FlashHotelFlightDealMerchandisingSlot'
import getEffectiveHotelFlightPrice from 'lib/offer/getEffectiveHotelFlightPrice'

function calculateOriginalValue({
  isMultiRoom,
  bundledWithFlightsOnly,
  hotelValue,
  flightPrice,
  multiRoomHotelValue,
  multiRoomFlightPrice,
  isFlightSelected,
}: {
  isMultiRoom: boolean
  bundledWithFlightsOnly: boolean
  hotelValue: number
  flightPrice: number
  multiRoomHotelValue: number
  multiRoomFlightPrice: number
  isFlightSelected: boolean
}): number {
  if (isMultiRoom) {
    if (bundledWithFlightsOnly) {
      return multiRoomHotelValue + multiRoomFlightPrice
    } else {
      return isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue
    }
  } else {
    if (bundledWithFlightsOnly) {
      return hotelValue + flightPrice
    } else {
      return hotelValue
    }
  }
}

const defaultMultiRoomTotals: MultiRoomPackageTotals = {
  flightPrice: 0,
  hotelMemberPrice: 0,
  hotelPlusFlightsMemberPrice: 0,
  hotelPlusFlightsPrice: 0,
  hotelPrice: 0,
  hotelValue: 0,
}

interface Props {
  offer: App.Offer
  rooms: Array<OfferPageRoom>
  cheapestOption: App.OfferPackageOptionView
  durationLabel: string
  flightOrigin?: App.Airport
  airports: Array<App.Airport>
  showHotelPriceWithFlights: boolean
  isFlightSelected: boolean
  isMultiRoom: boolean
  multiRoomPackageTotals: MultiRoomPackageTotals
  multiRoomPackages: PackageOptionViewByRoom
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
  fareClass: FlightsClassTypes
  calendarsLoading?: boolean
  bundledWithFlightsOnly: boolean
  checkIn?: string
}

function FlashOfferIntroPricingBlock(props: Props) {
  const {
    offer,
    cheapestOption: packageView,
    rooms,
    durationLabel,
    flightOrigin,
    airports,
    showHotelPriceWithFlights,
    isFlightSelected,
    isMultiRoom,
    multiRoomPackageTotals,
    multiRoomPackages,
    charterBundleRoute,
    priceMode,
    fareClass,
    calendarsLoading,
    bundledWithFlightsOnly,
    checkIn,
  } = props
  const {
    hotelPrice,
    hotelMemberPrice,
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    propertyFees,
    taxesAndFees,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const {
    hotelPrice: multiRoomHotelPrice,
    hotelMemberPrice: multiRoomHotelMemberPrice,
    hotelPlusFlightsPrice: multiRoomHotelFlightPrice,
    hotelPlusFlightsMemberPrice: multiRoomMemberFlightPrice,
    flightPrice: multiRoomFlightPrice,
    hotelValue: multiRoomHotelValue,
    shouldDisplayValue: multiRoomShouldDisplayValue,
  } = multiRoomPackageTotals

  const charterRoute = useMemo(
    () => (charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined),
    [charterBundleRoute],
  )
  const {
    highestDiscountPercent: multipleRoomHighestDiscountPercent,
  } = getHotelOfferDisplayedValue(!!bundledWithFlightsOnly, isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice, isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue, !!multiRoomShouldDisplayValue)

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const showPriceDetails = taxesAndFees > 0 || (propertyFees ?? 0) > 0 || config.agentHub.isEnabled

  // === Charter pricing logic (matching FlashOfferSummary) ===

  const {
    totalFlightPrice,
    regularTotalFlightPrice,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
  } = useOfferPageFlightPrice()
  const effectiveFlightPrice = getEffectiveHotelFlightPrice({
    checkIn,
    charterBundleRoute,
    flightOriginCode: flightOrigin?.code,
    offer: offer as App.HotelOffer,
    totalFlightPrice,
  })

  // Charter route: use charter flight price directly (not affected by flightCacheDisabled)
  // Non-charter route: use selected-date flight price or no-date cached offer flight price
  const flightPrice = charterRoute
    ? totalFlightPrice // Charter: only use charter price (undefined if not available)
    : effectiveFlightPrice || undefined
  const regularFlightPrice = charterRoute
    ? (regularTotalFlightPrice ?? totalFlightPrice)
    : effectiveFlightPrice || undefined

  // Compute pricing with flights using addFlightPrice utility. Maldives charter bundles
  // keep the hotel-only discount instead of diluting it with the undiscounted flight leg.
  const isCharterBundle = isMaldivesCharterBundleOffer(offer)
  const pricingWithFlights = regularFlightPrice ? addFlightPrice(hotelPricing, regularFlightPrice, isCharterBundle) : undefined
  const memberPricingWithFlights = (hotelMemberPricing && flightPrice) ? addFlightPrice(hotelMemberPricing, flightPrice, isCharterBundle) : undefined
  const summaryFlightPrice = regularFlightPrice ?? 0
  const hotelWithFlightsValue = pricingWithFlights?.value ?? hotelPricing.value + summaryFlightPrice

  const currentRegionHasFlights = useAppSelector(state => state.geo.currentRegionHasFlights)
  const flightsEnabled = !!flightOrigin && offer.offerFlightsEnabled && currentRegionHasFlights
  const hasFlights = !!pricingWithFlights && (!!charterRoute || !offer.flightCacheDisabled)
  const hideNoCacheMessaging = !hasFlights && bundledWithFlightsOnly

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !!checkIn && !!charterDepartDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkIn || !isCheckInMatchingCharterDate)

  // Get fallback pricing for charter bundle when no dates are selected
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass,
    nights: pkg.duration,
    disabled: !shouldUseFallbackPricing,
  })

  // Determine which pricing to display (matching FlashOfferSummary logic)
  let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
  if (bundledWithFlightsOnly) {
    if (!charterRoute && flightsEnabled && !hideNoCacheMessaging && hasFlights) {
      // Non-charter route: use standard flight pricing
      pricingToDisplay = pricingWithFlights
      memberPricingToDisplay = memberPricingWithFlights
    }
    else if (charterRoute) {
      if (fallbackPricing) {
        // No valid date selected - show fallback pricing
        pricingToDisplay = fallbackPricing
        memberPricingToDisplay = fallbackMemberPricing
      } else if (hasFlights) {
        // Valid date selected - show actual flight price
        pricingToDisplay = pricingWithFlights
        memberPricingToDisplay = memberPricingWithFlights
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    else {
      pricingToDisplay = undefined
      memberPricingToDisplay = undefined
    }
  }

  // Calculate shouldShowValue based on pricingToDisplay
  const shouldShowValue = useMemo(() => {
    if (isMultiRoom) {
      return multiRoomHotelValue > multiRoomHotelPrice && !!multiRoomShouldDisplayValue
    }
    if (pricingToDisplay) {
      return pricingToDisplay.value > pricingToDisplay.price && pkg.shouldDisplayValue
    }
    return hotelPricing.value > hotelPricing.price && pkg.shouldDisplayValue
  }, [isMultiRoom, multiRoomHotelValue, multiRoomHotelPrice, multiRoomShouldDisplayValue, pricingToDisplay, hotelPricing.value, hotelPricing.price, pkg.shouldDisplayValue])

  // Show discount pill based on pricingToDisplay
  const showDiscountPill = useMemo(() => {
    if (isMultiRoom) {
      const shouldShowDiscountPercent = multipleRoomHighestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
      return shouldShowDiscountPercent && !offer?.isDiscountPillHidden
    }
    if (pricingToDisplay) {
      return !offer?.isDiscountPillHidden && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    }
    const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    return !offer?.isDiscountPillHidden && ((shouldShowDiscountPercent && !bundledWithFlightsOnly) || (bundledWithFlightsOnly && shouldShowBundledDiscountPercent))
  }, [isMultiRoom, multipleRoomHighestDiscountPercent, offer?.isDiscountPillHidden, pricingToDisplay, hotelDiscountPercent, bundledDiscountPercent, bundledWithFlightsOnly])

  // Get effective discount percent from pricingToDisplay
  const effectiveDiscountPercent = useMemo(() => {
    if (pricingToDisplay && showDiscountPill) {
      return pricingToDisplay.discountPercent
    }
    if (!showDiscountPill) return undefined
    if (isMultiRoom) {
      return multipleRoomHighestDiscountPercent
    }
    return bundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent
  }, [pricingToDisplay, showDiscountPill, isMultiRoom, multipleRoomHighestDiscountPercent, bundledWithFlightsOnly, bundledDiscountPercent, hotelDiscountPercent])

  // Get original value from pricingToDisplay
  const originalValue = useMemo(() => {
    if (pricingToDisplay) {
      return pricingToDisplay.value
    }
    return calculateOriginalValue({
      isMultiRoom,
      bundledWithFlightsOnly: !!bundledWithFlightsOnly,
      hotelValue: hotelPricing.value,
      flightPrice: summaryFlightPrice,
      multiRoomHotelValue,
      multiRoomFlightPrice,
      isFlightSelected,
    })
  }, [pricingToDisplay, isMultiRoom, bundledWithFlightsOnly, hotelPricing.value, summaryFlightPrice, multiRoomHotelValue, multiRoomFlightPrice, isFlightSelected])

  // Legacy totalPrice/totalMemberPrice for non-charter routes and multi-room
  const totalPrice = useMemo(() => {
    let total = hotelPrice + (propertyFees ?? 0)
    if (bundledWithFlightsOnly && !charterRoute) {
      total += effectiveFlightPrice
    }
    return total
  }, [bundledWithFlightsOnly, charterRoute, effectiveFlightPrice, hotelPrice, propertyFees])

  const totalMemberPrice = useMemo(() => {
    let total = hotelMemberPrice
    if (hotelMemberPrice > 0) {
      total = hotelMemberPrice + (propertyFees ?? 0)
      if (bundledWithFlightsOnly && !charterRoute) {
        total += effectiveFlightPrice
      }
    }
    return total
  }, [bundledWithFlightsOnly, charterRoute, effectiveFlightPrice, hotelMemberPrice, propertyFees])

  // TODO: i18n – 💲 Pricing format: sale-unit strings ('package*', 'total', 'person*', 'person')
  // are part of a complex pricing display. Needs special handling or coordination with LuxPlusPriceStack.
  const saleUnit = useMemo(() => {
    const unit = bundledWithFlightsOnly ? 'package*' : 'total'
    if (priceMode === 'perPerson') {
      return unit.includes('*') ? 'person*' : 'person'
    }
    return unit
  }, [bundledWithFlightsOnly, priceMode])

  const { currentRegionCode } = useContext(GeoContext)

  const isRewardSelected = !!fareClass && isRewardFareClass(fareClass)
  const isReward = isRewardSelected && !!rewardAvailable && rewardSeatPointsTotal !== undefined && rewardSeatCashCoPayTotal !== undefined

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !bundledWithFlightsOnly || isReward,
    }),
  ], [offer, packageView, currentRegionCode, bundledWithFlightsOnly, isReward])

  const multiRoomLoyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration })
    ))
  }, [offer, multiRoomPackages, currentRegionCode])

  const multiRoomPointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...multiRoomLoyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice: multiRoomFlightPrice,
      skip: !isFlightSelected || isReward,
    }),
  ], [offer, isFlightSelected, multiRoomFlightPrice, multiRoomLoyaltyHotelOptions, isReward])

  const flightDestination = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const showFlightsDealTag = flightOrigin && flightDestination && deals.length > 0 && !charterBundleRoute

  const activeDeal = useMemo(
    () => (deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined),
    [deals],
  )

  // Use pricingToDisplay for charter routes, otherwise use multi-room or totalPrice logic
  const offerPrice = useMemo(() => {
    // For charter bundle with pricingToDisplay, use it directly
    if (charterRoute && pricingToDisplay) {
      return pricingToDisplay.price
    }
    if (isMultiRoom) {
      const multiRoomPrice = isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice
      return multiRoomPrice
    }
    return totalPrice
  }, [charterRoute, pricingToDisplay, isMultiRoom, isFlightSelected, multiRoomHotelFlightPrice, multiRoomHotelPrice, totalPrice])

  const offerMemberPrice = useMemo(() => {
    // For charter bundle with memberPricingToDisplay, use it directly
    if (charterRoute && memberPricingToDisplay) {
      return memberPricingToDisplay.price
    }
    if (isMultiRoom) {
      const multiRoomMemberPrice = isFlightSelected ? multiRoomMemberFlightPrice : multiRoomHotelMemberPrice
      return multiRoomMemberPrice
    }
    return totalMemberPrice
  }, [charterRoute, memberPricingToDisplay, isMultiRoom, isFlightSelected, multiRoomMemberFlightPrice, multiRoomHotelMemberPrice, totalMemberPrice])

  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])

  const intl = useIntl()
  const { travelSynonym } = useTravelSynonymLabels()
  const isWePriceMatchEnabled = !charterBundleRoute

  // For charter bundle routes, show loading state when:
  // 1. calendarsLoading is true (hotel prices loading), OR
  // 2. charterFlightPriceLoading is true AND we don't have pricingToDisplay yet
  const isCharterPriceLoading = charterRoute && calendarsLoading

  const extraGuestSurchargeText = intl.formatMessage({
    id: 'c49bMGV6K-aAi1z169XQ',
    defaultMessage: '+ Extra guest surcharge',
  })

  const hotelText = intl.formatMessage(productLabels.hotel)

  const hotelPlusFlightsText = intl.formatMessage({
    id: 'X8D4ik8z1J1eTsfpSfvV',
    defaultMessage: 'Hotel + flights',
  })

  const viewOptionsText = intl.formatMessage(
    {
      id: 'JaczHbGlU3Scp_uyLG7u',
      defaultMessage: 'View {travelSynonym} options',
    },
    {
      travelSynonym,
    },
  )

  if (showHotelPriceWithFlights && !isMultiRoom) {
    return <WalledContent enforced={offer.walledGarden}>
      <Group direction="vertical" gap={12} horizontalAlign="start">
        <Group direction="vertical" fullWidth gap={12}>
          {showDiscountPill && <PriceRowValueDiscount type="estimate" discountPercentage={hotelPricing.discountPercent} />}
          <AccommodationPackageTilePricingBlocks>
            <VerticalSpacer gap={12}>
              <BodyTextBlock variant="large" weight="bold">{hotelText}</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={convertToDisplay(totalMemberPrice)}
                    price={convertToDisplay(totalPrice)}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    testid="hotel-summary-price"
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description={extraGuestSurchargeText}
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={convertToDisplay(hotelPricing.value)}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={packageView.flightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">{viewOptionsText}</TextButton>}
                    />}
                  />
                </div>
              </VerticalSpacer>
            </VerticalSpacer>
            <VerticalSpacer gap={12}>
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">{hotelPlusFlightsText}</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice + summaryFlightPrice : 0)}
                    price={convertToDisplay(totalPrice + summaryFlightPrice)}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    testid="hotel-summary-price"
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description={extraGuestSurchargeText}
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={convertToDisplay(hotelWithFlightsValue)}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={summaryFlightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">{viewOptionsText}</TextButton>}
                    />}
                  />
                </div>
                {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
              </VerticalSpacer>
            </VerticalSpacer>
          </AccommodationPackageTilePricingBlocks>
        </Group>
        <FlashHotelFlightDealMerchandisingSlot
          flightOrigin={flightOrigin}
          flightDestination={flightDestination}
          deals={deals}
          hasFlights={hasFlights}
          disabled={!flightsEnabled || !!charterBundleRoute}
          allowCalculatedFallback
        />
        <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
      </Group>
    </WalledContent>
  }
  return <WalledContent enforced={offer.walledGarden}>
    <VerticalSpacer gap={8}>
      <div>
        {/* Show loading skeletons while charter bundle prices are loading */}
        {isCharterPriceLoading && <>
          <PriceRowPriceCaptionLoadingSkeleton />
          <PriceRowPriceLoadingSkeleton size="L" />
          <PriceRowValueDiscountWithCaptionLoadingSkeleton />
        </>}
        {/* Show actual prices when not loading */}
        {!isCharterPriceLoading && <>
          {!isMultiRoom && <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>}
          {isMultiRoom &&
            <PriceRowPriceCaption>
              <MobileMultipleRoomsDurationLabel shouldDisplayRoom />
            </PriceRowPriceCaption>}
          <LuxPlusPriceStack
            memberPrice={convertToDisplay(offerMemberPrice)}
            price={convertToDisplay(offerPrice)}
            size="L"
            saleUnit={saleUnit}
            offerConfig={offer.luxPlus}
            testid="hotel-summary-price"
          />
          {bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="light-foreground-alt">
            &nbsp;{intl.formatMessage({
              id: 'eKBIFKgS_aT0v8ga1o0H',
              defaultMessage: 'Two Adults',
            })}
          </Caption>}
          {showIncludedGuestsExceeded && (
            <AdditionalGuestsPopup
              complex={false}
              description={extraGuestSurchargeText}
              modalContent={pkg.roomPolicyDescription}
            />
          )}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="L"
            originalValue={convertToDisplay(originalValue)}
            discountPercentage={effectiveDiscountPercent}
          />}
          {!isMultiRoom && <PriceRowTaxesAndFees
            endIcon={showPriceDetails && <OfferPriceDetailsRow
              offerPage
              offer={offer}
              duration={pkg.duration}
              propertyFees={packageView.propertyFees}
              taxesAndFees={packageView.taxesAndFees}
              hotelPrice={packageView.hotelPrice}
              hotelMemberPrice={packageView.hotelMemberPrice}
              showMemberPrice={packageView.hotelMemberPrice > 0}
              flightPrice={bundledWithFlightsOnly ? summaryFlightPrice : undefined}
              rooms={rooms}
              extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
              dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
              trigger="info-icon"
              cta={
                <TextButton
                  kind="primary"
                  fit="flex"
                >
                  {viewOptionsText}
                </TextButton>
              }
            />}
          />}
        </>}
      </div>
      <Group direction="vertical" gap={8} horizontalAlign="start">
        <FlashHotelFlightDealMerchandisingSlot
          flightOrigin={flightOrigin}
          flightDestination={flightDestination}
          deals={deals}
          hasFlights={hasFlights}
          disabled={!flightsEnabled || !!charterBundleRoute}
          allowCalculatedFallback
        />
        {!isMultiRoom && <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" /> }
        {isMultiRoom && <LoyaltyPointsEarn calculationRequests={multiRoomPointsEarnCalculationRequests} calculationType="estimate" /> }
        {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
      </Group>
    </VerticalSpacer>
  </WalledContent>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    cheapestOption: getCheapestAvailablePackageOption(state, appState),
    rooms: state.rooms,
    isFlightSelected: state.flights,
    offer: state.offer,
    flightOrigin: state.airport,
    calendarsLoading: arePricesLoading(appState, state),
    isBNBLAvailable: isBNBLAvailable(state),
    isSpoofed: appState.auth.account.isSpoofed,
    durationLabel: getFromDurationLabel(state, appState),
    airports: appState.geo.airports,
    multiRoomPackages: getActivePackagesByRoom(state, appState),
    isMultiRoom,
    multiRoomPackageTotals: isMultiRoom ? getMultiRoomPackageTotals(state, appState) : defaultMultiRoomTotals,
    charterBundleRoute: getEffectiveCharterBundleRoute(state),
    priceMode: state.priceMode,
    fareClass: state.fareClass,
    bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
    checkIn: state.checkInDate,
  }
}, FlashOfferIntroPricingBlock)
